/* eslint-disable max-lines */
import { IntegrationSchema } from '@jupiterone/data-model';
import {
  EntityClass,
  EntityClassProperty,
  EntityDescribedProperty,
  EntityDescribedPropertyCategory,
  EntityDescribedPropertyFormat,
} from '../../types';
import {
  IPV4_REGEX,
  MARKDOWN_REGEX,
  MONEY_REGEX,
  TIME_PROPERTY_REGEX,
  URI_REGEX,
} from '../constants';
import parseAsJson from './parseAsJson';
const EXCLUDE_LIST = [
  '__iconPath',
  '__typename',
  '_typename',
  '_integrationDefinitionId',
  '_integrationInstanceId',
];

export function isExcludedProperty(property: string) {
  return EXCLUDE_LIST.indexOf(property) >= 0;
}

const TAG_KEY = 'tag.';

export function isTag(property: string): boolean {
  return property.indexOf(TAG_KEY) === 0;
}

export function isMetadata(propertyName: string): boolean {
  return propertyName.startsWith('_');
}

/*
 * Get the ID of a schema, either from a schema itself or from a schema
 * reference.
 */
function getRefId(ref: any): string {
  return ref.schema ? ref.schema.$id : ref.$id;
}

/*
 * Get the schema from a reference, which may have its own list of references or
 * (if at the edge of a reference graph) be itself a schema.
 */
function getRefSchema(ref: any): any {
  const properties =
    ref.schema?.allOf && ref.schema.allOf.find((ref: any) => ref.properties);
  if (properties) {
    return properties;
  } else if (ref.schema) {
    return ref.schema;
  } else {
    return ref;
  }
}

function entityClassPropertiesFromSchema(
  /*
   * Props that we have collected from other schemas.
   */
  props: EntityClassProperty[],
  /*
   * A list of properties that have been excluded at some point in the schema
   * hierarchy.
   */
  excludes: string[],
  id: string,
  schema: any
): EntityClassProperty[] {
  if (!schema.properties) {
    return [];
  }
  return Object.entries<EntityClassProperty>(schema.properties).reduce(
    (
      withNames,
      [name, { name: entityPropertyName, required, ...entityProperty }]: [
        string,
        EntityClassProperty
      ]
    ) => {
      if (props.find((p) => p.name === name) || excludes.includes(name)) {
        return withNames;
      }
      return [
        ...withNames,
        {
          // We don't want the ajv implementation detail of pounds for IDs to
          // leak out of this file.
          source: id.slice(1),
          name: name || entityPropertyName,
          required:
            required ||
            (schema.required ? schema.required.includes(name) : false),
          ...entityProperty,
        },
      ];
    },
    [] as EntityClassProperty[]
  );
}

function _classPropertiesFromSchema(
  rootId: string,
  schemaRefs: any[],
  addedClasses: string[] = [],
  addedProps: EntityClassProperty[] = []
): [EntityClassProperty[], string[]] {
  let excludes: string[] = [];

  return [
    schemaRefs.reduce((props, ref) => {
      const refId = getRefId(ref);
      const refSchema = getRefSchema(ref);
      excludes = excludes.concat(refSchema.excludes);

      if (addedClasses.includes(refId)) {
        return props;
      }
      addedClasses.push(refId);

      const refPropsListWithNames = entityClassPropertiesFromSchema(
        addedProps.concat(props),
        excludes,
        refId,
        refSchema
      );

      let nestedRefProps: EntityClassProperty[] = [];
      const nestedRefs = ref.refVal;
      if (refId !== rootId && nestedRefs) {
        [nestedRefProps] = _classPropertiesFromSchema(
          refId,
          nestedRefs,
          addedClasses,
          addedProps.concat(props)
        );
      }

      return [...nestedRefProps, ...props, ...refPropsListWithNames];
    }, []),
    addedClasses,
  ];
}

function classPropertiesFromSchema(
  rootId: string,
  schemaRefs: any[]
): EntityClassProperty[] {
  const [calculatedProperties] = _classPropertiesFromSchema(rootId, schemaRefs);
  return calculatedProperties;
}

export function entityClassFromSchema(
  fromClass: string
): EntityClass | undefined {
  const validate = IntegrationSchema.getSchema('#' + fromClass);

  if (!validate || !(validate && validate.schema && validate.refVal)) {
    return undefined;
  }

  /*
   * In ajv, schemas can be objects or booleans (a true or false schema always
   * validates or never validates, respectively). However, we do not use this
   * feature in any of our schemas, so we can safely cast to any
   * .
   */
  const schemaObject = validate.schema as any;

  return {
    description: schemaObject.description,
    type: schemaObject.$id.slice(1),
    properties: classPropertiesFromSchema(schemaObject.$id, validate.refVal),
  };
}

export function allSchemas(): string[] {
  /*
   * We use the _schemas property on the ajv schema to get all schemas (in the
   * form of a map from name to schema). Unfortunately, it isn't on the type, so
   * we cast to any. Probably the most interesting thing here is the slice,
   * which we do to get rid of the default ajv base schema
   * ("http://json-schema.org/draft-07/schema"). We're also sorting and then
   * removing the leading pound symbol that ajv uses for ids.
   */
  return Object.keys((IntegrationSchema as any)._schemas)
    .slice(1)
    .sort()
    .map((schemaId) => schemaId.slice(1));
}

/*
 * A list of schemas that should not be displayed to the user.
 */
export function metaSchemas(): string[] {
  return ['Root', 'Internet', 'RecordEntity', 'Entity', 'GraphObject'];
}

export function describedEntityProperties(
  properties: any,
  classes?: string[] | null
): EntityDescribedProperty[] {
  const EntityDefaults = entityClassFromSchema(classes?.[0] || 'Entity');
  const overrides = {};
  const describedProperties: EntityDescribedProperty[] =
    EntityDefaults?.properties
      // Remove invalid props that entityClassFromSchema returns
      .filter((defaultProp) => !['tag.*', 'tags'].includes(defaultProp.name))
      .map((defaultProp) => {
        return {
          description: defaultProp.description,
          name: defaultProp.name,
          required: defaultProp.required,
          source: defaultProp.source || 'Unknown',
          type: getPropertyFormat(
            properties[defaultProp.name],
            defaultProp.name,
            defaultProp.format,
            defaultProp.type
          ),
          value: properties[defaultProp.name],
          category: getCategory(defaultProp.name),
          edited: false,
          isSchemaOnly: !Object.prototype.hasOwnProperty.call(
            properties,
            defaultProp.name
          ),
        };
      }) || [];
  // Find all the @props and user defined props
  Object.keys(properties).forEach((key) => {
    if (isExcludedProperty(key)) return;
    if (key[0] === '@') {
      const trueKey = key.substring(1, key.length);
      return ((overrides as any)[trueKey] = properties[key]);
    }
    const category = getCategory(key);
    const schemaProp = describedProperties.find(
      ({ name }: { name: string }) => name === key
    );
    if (!schemaProp) {
      describedProperties.push({
        description:
          category === EntityDescribedPropertyCategory.METADATA
            ? 'System Defined'
            : 'User Defined',
        name: key,
        required: false,
        source: 'Custom',
        type: getPropertyFormat(properties[key], key),
        value: properties[key],
        category,
        edited: false,
        isSchemaOnly: false,
      });
    }
  });
  return describedProperties.map((prop) => {
    prop.originalValue = (overrides as any)[prop.name];
    prop.edited = prop.name in overrides; //originalValue may be undefined
    return prop;
  });
}
const getCategory = (key: string) => {
  return isMetadata(key)
    ? EntityDescribedPropertyCategory.METADATA
    : isTag(key)
    ? EntityDescribedPropertyCategory.TAG
    : EntityDescribedPropertyCategory.PROPERTY;
};
// eslint-disable-next-line complexity
const getPropertyFormat = (
  value: unknown,
  name: string,
  schemaFormat?: string,
  schemaType?: string
): EntityDescribedPropertyFormat | EntityDescribedPropertyFormat[] => {
  switch (schemaFormat) {
    case 'uri':
      return EntityDescribedPropertyFormat.URI;
    case 'email':
      return EntityDescribedPropertyFormat.EMAIL;
    case 'ip':
      return EntityDescribedPropertyFormat.IP;
    case 'ipv4':
      return EntityDescribedPropertyFormat.IPV4;
    case 'ipv6':
      return EntityDescribedPropertyFormat.IPV6;
    case 'date-time':
      return EntityDescribedPropertyFormat.DATETIME;
    case 'utc-date':
      return EntityDescribedPropertyFormat.UTCDATE;
    case 'iso-date':
      return EntityDescribedPropertyFormat.ISODATE;
    case 'date':
      return EntityDescribedPropertyFormat.DATE;
    case 'local-time':
      return EntityDescribedPropertyFormat.LOCALTIME;
  }

  switch (schemaType) {
    case 'integer':
    case 'number':
      return EntityDescribedPropertyFormat.NUMBER;
    case 'string':
      return EntityDescribedPropertyFormat.STRING;
    case 'boolean':
      return EntityDescribedPropertyFormat.BOOLEAN;
    case 'array':
      if (!Array.isArray(value)) return EntityDescribedPropertyFormat.UNDEFINED;
      return value.map((v: any) => getPropertyFormat(v, name)).flat();
  }

  if (value === undefined) return EntityDescribedPropertyFormat.UNDEFINED;
  if (value === null) return EntityDescribedPropertyFormat.NULL;

  if (Array.isArray(value))
    return value.map((v) => getPropertyFormat(v, name)).flat();

  switch (typeof value) {
    case 'object': // All other object types have been parsed
      return EntityDescribedPropertyFormat.JSON;
    case 'boolean':
      return EntityDescribedPropertyFormat.BOOLEAN;
    case 'number':
      if (TIME_PROPERTY_REGEX.exec(name))
        return EntityDescribedPropertyFormat.ISODATE;
      if (MONEY_REGEX.exec(name)) return EntityDescribedPropertyFormat.MONEY;
      return EntityDescribedPropertyFormat.NUMBER;
    case 'string':
      if (parseAsJson(value)) return EntityDescribedPropertyFormat.JSON;
      if (IPV4_REGEX.exec(value)) return EntityDescribedPropertyFormat.IP;
      if (URI_REGEX.exec(value)) return EntityDescribedPropertyFormat.URI;
      if (MARKDOWN_REGEX.exec(value) && value.includes('\n'))
        return EntityDescribedPropertyFormat.MARKDOWN;
      return EntityDescribedPropertyFormat.STRING;
    default:
      return EntityDescribedPropertyFormat.UNKNOWN;
  }
};
